class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

(function () {
  if (window.RYASmartSearch) return;

  const SEARCH_SELECTOR = 'form[action*="/search"] input[name="q"], form[role="search"] input[name="q"]';
  const STOP_WORDS = new Set([
    'a',
    'al',
    'auto',
    'carro',
    'con',
    'de',
    'del',
    'el',
    'en',
    'la',
    'las',
    'lo',
    'los',
    'mi',
    'para',
    'por',
    'repuesto',
    'repuestos',
    'sin',
    'the',
    'un',
    'una',
    'y',
  ]);

  const BRAND_GROUPS = [
    ['toyota', 'toy'],
    ['honda'],
    ['nissan'],
    ['hyundai', 'hyunda'],
    ['kia'],
    ['mazda'],
    ['mitsubishi'],
    ['ford'],
    ['chevrolet', 'chevy', 'geo'],
    ['suzuki'],
    ['isuzu'],
    ['daihatsu'],
    ['jeep'],
    ['dodge'],
    ['mercedes', 'benz'],
    ['bmw'],
    ['audi'],
    ['volkswagen', 'vw'],
    ['subaru'],
    ['hino'],
    ['mini'],
  ];

  const PART_GROUPS = [
    {
      id: 'brake_pad',
      canonical: 'Pastilla de Freno',
      aliases: ['pastilla', 'pastillas', 'balata', 'balatas', 'friccion', 'fricciones', 'pads', 'brake pad', 'brake pads'],
    },
    {
      id: 'brake_shoe',
      canonical: 'Zapata de Freno',
      aliases: ['zapata', 'zapatas', 'banda', 'bandas', 'brake shoe', 'brake shoes'],
    },
    {
      id: 'brake_disc',
      canonical: 'Disco de Freno',
      aliases: ['disco', 'discos', 'rotor', 'rotores'],
    },
    {
      id: 'brake_booster',
      canonical: 'Booster Freno',
      aliases: ['booster', 'boster', 'hidrovac', 'servo freno', 'servofreno'],
    },
    {
      id: 'caliper',
      canonical: 'Caliper',
      aliases: ['caliper', 'calipers', 'mordaza', 'mordazas'],
    },
    {
      id: 'brake_fluid',
      canonical: 'Liquido de Frenos',
      aliases: ['liquido de freno', 'liquido de frenos', 'brake fluid'],
    },
    {
      id: 'brake_general',
      canonical: 'Freno',
      aliases: ['freno', 'frenos', 'brake', 'brakes', 'sistema de freno', 'sistema de frenado'],
    },
    {
      id: 'headlight',
      canonical: 'Faro',
      aliases: ['faro', 'faros', 'foco', 'focos', 'farol', 'faroles', 'lampara', 'lamparas', 'luz', 'luces', 'bombillo', 'bombillos', 'headlight', 'headlights', 'headlamp'],
    },
    {
      id: 'tail_light',
      canonical: 'Stop',
      aliases: ['stop', 'stops', 'luz trasera', 'calavera', 'calaveras', 'tail light', 'tail lamp'],
    },
    {
      id: 'spark_plug',
      canonical: 'Bujia',
      aliases: ['bujia', 'bujias', 'spark plug', 'spark plugs', 'candela', 'candelas'],
    },
    {
      id: 'coil',
      canonical: 'Bobina',
      aliases: ['bobina', 'bobinas', 'coil', 'coils'],
    },
    {
      id: 'oil_filter',
      canonical: 'Filtro de Aceite',
      aliases: ['filtro aceite', 'filtro de aceite', 'oil filter'],
    },
    {
      id: 'air_filter',
      canonical: 'Filtro de Aire',
      aliases: ['filtro aire', 'filtro de aire', 'air filter'],
    },
    {
      id: 'cabin_filter',
      canonical: 'Filtro de Cabina',
      aliases: ['filtro cabina', 'filtro de cabina', 'filtro ac', 'filtro a c', 'cabin filter'],
    },
    {
      id: 'fuel_filter',
      canonical: 'Filtro de Gasolina',
      aliases: ['filtro gasolina', 'filtro de gasolina', 'filtro combustible', 'fuel filter'],
    },
    {
      id: 'fuel_pump',
      canonical: 'Bomba de Gasolina',
      aliases: ['bomba gasolina', 'bomba de gasolina', 'bomba combustible', 'fuel pump'],
    },
    {
      id: 'water_pump',
      canonical: 'Bomba de Agua',
      aliases: ['bomba agua', 'bomba de agua', 'water pump'],
    },
    {
      id: 'alternator',
      canonical: 'Alternador',
      aliases: ['alternador', 'alternadora', 'alternator'],
    },
    {
      id: 'starter',
      canonical: 'Motor de Arranque',
      aliases: ['motor arranque', 'motor de arranque', 'starter', 'burro'],
    },
    {
      id: 'radiator',
      canonical: 'Radiador',
      aliases: ['radiador', 'radiadores', 'radiator'],
    },
    {
      id: 'hose',
      canonical: 'Manguera',
      aliases: ['manguera', 'mangueras', 'hose', 'hoses'],
    },
    {
      id: 'shock',
      canonical: 'Amortiguador',
      aliases: ['amortiguador', 'amortiguadores', 'amortiguacion', 'shock', 'shocks', 'strut', 'struts'],
    },
    {
      id: 'bushing',
      canonical: 'Buje de Suspension',
      aliases: ['buje', 'bujes', 'bushing', 'bushings', 'hule suspension', 'hules suspension'],
    },
    {
      id: 'ball_joint',
      canonical: 'Rotula',
      aliases: ['rotula', 'rotulas', 'ball joint', 'ball joints'],
    },
    {
      id: 'tie_rod',
      canonical: 'Terminal',
      aliases: ['terminal', 'terminales', 'cabezal', 'cabezales', 'tie rod', 'rack end'],
    },
    {
      id: 'bearing',
      canonical: 'Balinera',
      aliases: ['balinera', 'balineras', 'cojinete', 'cojinetes', 'bearing', 'bearings'],
    },
    {
      id: 'clutch',
      canonical: 'Clutch',
      aliases: ['clutch', 'embrague', 'kit clutch', 'kit de clutch'],
    },
    {
      id: 'oxygen_sensor',
      canonical: 'Sensor de Oxigeno',
      aliases: ['sensor oxigeno', 'sensor de oxigeno', 'o2 sensor', 'sensor o2', 'lambda'],
    },
  ];

  const PART_ALIAS_TOKENS = new Set();
  PART_GROUPS.forEach((group) => {
    group.aliases.concat(group.canonical).forEach((alias) => {
      normalize(alias).split(' ').forEach((token) => {
        if (token) PART_ALIAS_TOKENS.add(token);
      });
    });
  });

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' y ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function tokenMatches(haystack, token) {
    if (!token) return false;
    const compact = haystack.replace(/\s+/g, '');
    const compactToken = token.replace(/\s+/g, '');
    return haystack.indexOf(token) !== -1 || compact.indexOf(compactToken) !== -1;
  }

  function detectPartGroups(normalizedQuery) {
    return PART_GROUPS.filter((group) =>
      group.aliases.concat(group.canonical).some((alias) => {
        const normalizedAlias = normalize(alias);
        if (!normalizedAlias) return false;
        return normalizedQuery === normalizedAlias || normalizedQuery.indexOf(normalizedAlias) !== -1;
      })
    );
  }

  function detectBrands(tokens) {
    return BRAND_GROUPS.filter((group) => group.some((brand) => tokens.includes(brand)));
  }

  function getYears(tokens) {
    return tokens
      .map((token) => parseInt(token, 10))
      .filter((year) => year >= 1950 && year <= 2035);
  }

  function getModelTokens(tokens, partGroups, brandGroups) {
    const brands = new Set();
    brandGroups.forEach((group) => group.forEach((brand) => brands.add(brand)));

    return tokens.filter((token) => {
      if (!token || token.length < 2) return false;
      if (STOP_WORDS.has(token)) return false;
      if (PART_ALIAS_TOKENS.has(token)) return false;
      if (brands.has(token)) return false;
      if (/^(19|20)\d{2}$/.test(token)) return false;
      if (/^\d+(\.\d+)?$/.test(token)) return false;
      return !['automatico', 'manual', 'delantero', 'trasero', 'derecho', 'izquierdo', 'frontal', 'rear', 'front'].includes(token);
    });
  }

  function analyzeQuery(query) {
    const normalized = normalize(query);
    const tokens = normalized ? normalized.split(' ') : [];
    const partGroups = detectPartGroups(normalized);
    const brandGroups = detectBrands(tokens);
    const years = getYears(tokens);
    const modelTokens = getModelTokens(tokens, partGroups, brandGroups);

    return {
      original: String(query || '').trim(),
      normalized,
      tokens,
      partGroups,
      brandGroups,
      years,
      modelTokens,
    };
  }

  function buildShopifyQuery(query) {
    const info = analyzeQuery(query);
    if (!info.normalized) return '';

    if (!info.partGroups.length) {
      return info.original.replace(/\s+/g, ' ').trim();
    }

    const part = info.partGroups[0].canonical;
    const vehicleTokens = info.tokens.filter((token) => {
      if (!token) return false;
      if (STOP_WORDS.has(token)) return false;
      if (PART_ALIAS_TOKENS.has(token)) return false;
      return true;
    });

    const seen = new Set();
    const vehicleText = vehicleTokens
      .filter((token) => {
        if (seen.has(token)) return false;
        seen.add(token);
        return true;
      })
      .join(' ');

    return [part, vehicleText].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  }

  function getQueryVariants(query) {
    const info = analyzeQuery(query);
    const variants = [];
    const add = (term) => {
      const clean = String(term || '').replace(/\s+/g, ' ').trim();
      if (!clean) return;
      if (variants.some((item) => normalize(item) === normalize(clean))) return;
      variants.push(clean);
    };

    const smart = buildShopifyQuery(query);
    add(smart);
    add(info.original);

    if (info.partGroups.length) {
      const part = info.partGroups[0].canonical;
      const brandText = info.brandGroups.length ? info.brandGroups[0][0] : '';
      const modelText = info.modelTokens.join(' ');
      const yearText = info.years[0] ? String(info.years[0]) : '';

      add([part, brandText, modelText, yearText].filter(Boolean).join(' '));
      add([part, brandText, modelText].filter(Boolean).join(' '));
      add([part, modelText, yearText].filter(Boolean).join(' '));
      add([part, modelText].filter(Boolean).join(' '));
      add([part, brandText].filter(Boolean).join(' '));
    }

    return variants.slice(0, 6);
  }

  function productHaystack(product) {
    const tags = Array.isArray(product.tags) ? product.tags.join(' ') : product.tags || '';
    return normalize([
      product.title,
      product.type,
      product.product_type,
      product.vendor,
      product.handle,
      tags,
      product.body,
    ].join(' '));
  }

  function productHasPart(product, group) {
    const haystack = productHaystack(product);
    return group.aliases.concat(group.canonical).some((alias) => tokenMatches(haystack, normalize(alias)));
  }

  function productHasBrand(product, brandGroup) {
    const haystack = productHaystack(product);
    return brandGroup.some((brand) => tokenMatches(haystack, normalize(brand)));
  }

  function productSupportsYear(product, year) {
    const raw = String([
      product.title,
      product.type,
      product.product_type,
      Array.isArray(product.tags) ? product.tags.join(' ') : product.tags || '',
      product.body,
    ].join(' ')).toLowerCase();

    const ranges = [];
    let match;
    const closedRange = /\b(19[5-9]\d|20[0-3]\d)\s*(?:-|\/|\sa\s|\sal\s|\shasta\s)\s*(19[5-9]\d|20[0-3]\d)?/g;
    while ((match = closedRange.exec(raw))) {
      const start = parseInt(match[1], 10);
      const end = match[2] ? parseInt(match[2], 10) : 2035;
      ranges.push([Math.min(start, end), Math.max(start, end)]);
    }

    const decadeRange = /\b(19[5-9]|20[0-3])0s\b/g;
    while ((match = decadeRange.exec(raw))) {
      const start = parseInt(match[1] + '0', 10);
      ranges.push([start, start + 9]);
    }

    if (ranges.length) {
      return ranges.some(([start, end]) => year >= start && year <= end);
    }

    return raw.indexOf(String(year)) !== -1 ? true : null;
  }

  function scoreProduct(product, info) {
    const haystack = productHaystack(product);
    let score = 0;
    const reasons = [];

    if (info.partGroups.length) {
      const matchingPart = info.partGroups.find((group) => productHasPart(product, group));
      if (!matchingPart) return null;
      score += 120;
      reasons.push(matchingPart.canonical);
    }

    if (info.brandGroups.length) {
      const matchingBrand = info.brandGroups.find((group) => productHasBrand(product, group));
      if (!matchingBrand) return null;
      score += 50;
      reasons.push(matchingBrand[0]);
    }

    if (info.modelTokens.length) {
      const matchedModels = info.modelTokens.filter((token) => tokenMatches(haystack, token));
      if (!matchedModels.length) return null;
      score += matchedModels.length * 60;
      reasons.push(matchedModels.join(' '));
    }

    if (info.years.length) {
      const supported = info.years.some((year) => productSupportsYear(product, year) === true);
      const rejected = info.years.some((year) => productSupportsYear(product, year) === false);
      if (rejected && !supported) return null;
      if (supported) {
        score += 70;
        reasons.push(String(info.years[0]));
      }
    }

    info.tokens.forEach((token) => {
      if (token.length > 2 && tokenMatches(haystack, token)) score += 8;
    });

    if (product.available === true) score += 8;
    if (normalize(product.title).indexOf(info.normalized) !== -1) score += 40;

    return { product, score, reasons };
  }

  function dedupeProducts(scoredProducts) {
    const seen = new Set();
    return scoredProducts
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .filter((entry) => {
        const key = entry.product.id || entry.product.handle || entry.product.url || entry.product.title;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  function fetchSuggest(term, limit) {
    const params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(limit || 10));
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    return fetch('/search/suggest.json?' + params.toString(), { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => {
        const results = data && data.resources && data.resources.results;
        return results && results.products ? results.products : [];
      })
      .catch(() => []);
  }

  function smartSuggest(query, limit) {
    const info = analyzeQuery(query);
    const variants = getQueryVariants(query);

    return Promise.all(variants.map((term) => fetchSuggest(term, limit || 10))).then((groups) => {
      const scored = [];
      groups.flat().forEach((product) => {
        const entry = scoreProduct(product, info);
        if (entry) scored.push(entry);
      });
      return dedupeProducts(scored).slice(0, limit || 10);
    });
  }

  function formatPrice(product) {
    const raw = product.price_min || product.price || '';
    if (raw === '' || raw === null || raw === undefined) return '';
    const number = Number(raw);
    if (!Number.isFinite(number)) return String(raw);
    return 'L ' + number.toFixed(2);
  }

  function getProductImage(product) {
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (product.image) return product.image;
    if (Array.isArray(product.images) && product.images[0]) return product.images[0].src || product.images[0];
    return '';
  }

  function injectStyles() {
    if (document.getElementById('rya-smart-search-style')) return;

    const style = document.createElement('style');
    style.id = 'rya-smart-search-style';
    style.textContent = `
      .rya-smart-panel {
        width: min(1120px, calc(100% - 32px));
        margin: 0 auto 36px;
        padding: 22px;
        border: 1px solid rgba(18, 18, 18, 0.12);
        border-radius: 18px;
        background: #101114;
        color: #fff;
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.16);
      }
      .rya-smart-panel__eyebrow {
        margin: 0 0 6px;
        color: #d5db24;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .rya-smart-panel h2 {
        margin: 0 0 8px;
        color: #fff;
        font-size: clamp(24px, 4vw, 38px);
        line-height: 1.06;
      }
      .rya-smart-panel__copy {
        max-width: 760px;
        margin: 0 0 18px;
        color: rgba(255, 255, 255, 0.78);
        font-size: 16px;
        line-height: 1.45;
      }
      .rya-smart-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 0 0 20px;
      }
      .rya-smart-chip {
        display: inline-flex;
        align-items: center;
        min-height: 32px;
        padding: 7px 12px;
        border-radius: 999px;
        background: rgba(213, 219, 36, 0.14);
        color: #f7fb83;
        font-size: 13px;
        font-weight: 750;
      }
      .rya-smart-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
        gap: 14px;
      }
      .rya-smart-card {
        display: grid;
        grid-template-columns: 84px 1fr;
        gap: 12px;
        min-height: 120px;
        padding: 12px;
        border-radius: 14px;
        border: 1px solid rgba(255, 255, 255, 0.14);
        background: rgba(255, 255, 255, 0.06);
        color: #fff;
        text-decoration: none;
      }
      .rya-smart-card:hover {
        border-color: rgba(213, 219, 36, 0.72);
        background: rgba(255, 255, 255, 0.1);
      }
      .rya-smart-card img,
      .rya-smart-card__placeholder {
        width: 84px;
        height: 84px;
        object-fit: contain;
        border-radius: 12px;
        background: #fff;
      }
      .rya-smart-card__placeholder {
        background: linear-gradient(135deg, #2a2c31, #18191d);
      }
      .rya-smart-card__vendor {
        color: #d5db24;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .rya-smart-card__title {
        margin-top: 2px;
        color: #fff;
        font-size: 14px;
        font-weight: 800;
        line-height: 1.22;
      }
      .rya-smart-card__meta {
        margin-top: 8px;
        color: rgba(255, 255, 255, 0.7);
        font-size: 12px;
        line-height: 1.35;
      }
      .rya-smart-card__price {
        margin-top: 7px;
        color: #fff;
        font-weight: 900;
      }
      .rya-smart-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 18px;
      }
      .rya-smart-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 44px;
        padding: 10px 16px;
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #fff;
        font-weight: 850;
        text-decoration: none;
      }
      .rya-smart-button--primary {
        border-color: #25d366;
        background: #25d366;
        color: #07140c;
      }
      html.rya-smart-has-results .template-search__header p[role="status"],
      html.rya-smart-has-results .template-search--empty .title-wrapper.center {
        display: none !important;
      }
      @media screen and (max-width: 749px) {
        .rya-smart-panel {
          width: calc(100% - 24px);
          margin-bottom: 28px;
          padding: 18px;
          border-radius: 16px;
        }
        .rya-smart-grid {
          grid-template-columns: 1fr;
        }
        .rya-smart-card {
          grid-template-columns: 76px 1fr;
        }
        .rya-smart-card img,
        .rya-smart-card__placeholder {
          width: 76px;
          height: 76px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function renderProductCards(entries) {
    return entries
      .map(({ product, reasons }) => {
        const image = getProductImage(product);
        const price = formatPrice(product);
        const meta = reasons.length ? 'Coincide con: ' + reasons.slice(0, 3).join(', ') : 'Producto relacionado';

        return `
          <a class="rya-smart-card" href="${escapeHtml(product.url || '#')}">
            <div>
              ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<div class="rya-smart-card__placeholder"></div>'}
            </div>
            <div>
              <div class="rya-smart-card__vendor">${escapeHtml(product.vendor || 'RepuestosYA')}</div>
              <div class="rya-smart-card__title">${escapeHtml(product.title)}</div>
              <div class="rya-smart-card__meta">${escapeHtml(meta)}</div>
              ${price ? `<div class="rya-smart-card__price">${escapeHtml(price)}</div>` : ''}
            </div>
          </a>
        `;
      })
      .join('');
  }

  function renderSearchPagePanel(query, entries) {
    injectStyles();

    const existing = document.querySelector('[data-rya-smart-search-panel]');
    if (existing) existing.remove();

    const container = document.querySelector('#ProductGridContainer') || document.querySelector('.template-search') || document.querySelector('main');
    if (!container || !query) return;

    const smartQuery = buildShopifyQuery(query);
    const panel = document.createElement('section');
    panel.className = 'rya-smart-panel';
    panel.setAttribute('data-rya-smart-search-panel', 'true');

    if (entries.length) {
      document.documentElement.classList.add('rya-smart-has-results');
      panel.innerHTML = `
        <p class="rya-smart-panel__eyebrow">Busqueda inteligente RepuestosYA</p>
        <h2>Encontramos piezas compatibles</h2>
        <p class="rya-smart-panel__copy">Tu busqueda fue entendida como "${escapeHtml(smartQuery)}". Revisamos sinonimos, marca, modelo y rango de anio para mostrar piezas que si tienen sentido para el carro.</p>
        <div class="rya-smart-chips">
          ${getQueryVariants(query).slice(0, 4).map((term) => `<span class="rya-smart-chip">${escapeHtml(term)}</span>`).join('')}
        </div>
        <div class="rya-smart-grid">${renderProductCards(entries)}</div>
        <div class="rya-smart-actions">
          <a class="rya-smart-button" href="/search?type=product&q=${encodeURIComponent(smartQuery)}">Ver busqueda completa</a>
          <a class="rya-smart-button rya-smart-button--primary" href="${getWhatsAppUrl(query)}">Confirmar por WhatsApp</a>
        </div>
      `;
    } else {
      document.documentElement.classList.remove('rya-smart-has-results');
      panel.innerHTML = `
        <p class="rya-smart-panel__eyebrow">Busqueda inteligente RepuestosYA</p>
        <h2>No encontramos una coincidencia segura</h2>
        <p class="rya-smart-panel__copy">Buscamos "${escapeHtml(query)}" usando sinonimos como "${escapeHtml(smartQuery)}". Para evitar venderte una pieza equivocada, mandanos la busqueda por WhatsApp y te confirmamos disponibilidad o la alternativa correcta.</p>
        <div class="rya-smart-actions">
          <a class="rya-smart-button rya-smart-button--primary" href="${getWhatsAppUrl(query)}">Enviar busqueda por WhatsApp</a>
          <a class="rya-smart-button" href="/search?type=product&q=${encodeURIComponent(smartQuery)}">Probar busqueda mejorada</a>
        </div>
      `;
    }

    container.parentNode.insertBefore(panel, container);
  }

  function getWhatsAppUrl(query) {
    const message = 'Hola RepuestosYA, busco este repuesto: ' + query + '. Me pueden confirmar compatibilidad y disponibilidad?';
    return 'https://wa.me/50433899071?text=' + encodeURIComponent(message);
  }

  function enhanceForms() {
    document.querySelectorAll(SEARCH_SELECTOR).forEach((input) => {
      const form = input.closest('form');
      if (!form || form.dataset.ryaSmartSubmit === 'true') return;
      form.dataset.ryaSmartSubmit = 'true';

      form.addEventListener('submit', () => {
        const original = input.value.trim();
        const smart = buildShopifyQuery(original);
        if (smart && normalize(smart) !== normalize(original)) {
          try {
            sessionStorage.setItem('rya:lastOriginalSearch', original);
          } catch (error) {
            // Ignore private browsing storage failures.
          }
          input.value = smart;
        }
      });
    });
  }

  function enhanceCurrentSearchPage() {
    if (location.pathname.indexOf('/search') === -1) return;

    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    if (!query.trim()) return;

    let original = query;
    try {
      const storedOriginal = sessionStorage.getItem('rya:lastOriginalSearch');
      if (storedOriginal && normalize(buildShopifyQuery(storedOriginal)) === normalize(query)) {
        original = storedOriginal;
      }
    } catch (error) {
      original = query;
    }

    const smart = buildShopifyQuery(original);
    const nativeItems = document.querySelectorAll('#product-grid .grid__item').length;

    if (nativeItems > 0 && normalize(smart) === normalize(query)) return;

    injectStyles();
    const loadingPanel = document.createElement('section');
    loadingPanel.className = 'rya-smart-panel';
    loadingPanel.setAttribute('data-rya-smart-search-panel', 'true');
    loadingPanel.innerHTML = `
      <p class="rya-smart-panel__eyebrow">Busqueda inteligente RepuestosYA</p>
      <h2>Buscando por compatibilidad...</h2>
      <p class="rya-smart-panel__copy">Estamos probando sinonimos, marca, modelo y anio para rescatar resultados utiles.</p>
    `;

    const container = document.querySelector('#ProductGridContainer') || document.querySelector('.template-search') || document.querySelector('main');
    if (container && !document.querySelector('[data-rya-smart-search-panel]')) {
      container.parentNode.insertBefore(loadingPanel, container);
    }

    smartSuggest(original, 12).then((entries) => renderSearchPagePanel(original, entries));
  }

  function installSuggestFetchRewrite() {
    if (window.fetch.dataset && window.fetch.dataset.ryaSmartWrapped) return;

    const nativeFetch = window.fetch.bind(window);
    const wrappedFetch = function (resource, init) {
      try {
        const originalUrl = typeof resource === 'string' ? resource : resource && resource.url;
        if (originalUrl) {
          const url = new URL(originalUrl, window.location.origin);
          if (url.pathname.indexOf('/search/suggest') !== -1 && url.searchParams.has('q') && !url.searchParams.has('rya_no_smart')) {
            const originalQuery = url.searchParams.get('q') || '';
            const smartQuery = buildShopifyQuery(originalQuery);
            if (smartQuery && normalize(smartQuery) !== normalize(originalQuery)) {
              url.searchParams.set('q', smartQuery);
              if (typeof resource === 'string') {
                const nextUrl = url.origin === window.location.origin ? url.pathname + url.search : url.toString();
                return nativeFetch(nextUrl, init);
              }
              if (resource instanceof Request) {
                return nativeFetch(new Request(url.toString(), resource), init);
              }
            }
          }
        }
      } catch (error) {
        return nativeFetch(resource, init);
      }

      return nativeFetch(resource, init);
    };

    wrappedFetch.dataset = { ryaSmartWrapped: 'true' };
    window.fetch = wrappedFetch;
  }

  function initSmartSearch() {
    installSuggestFetchRewrite();
    enhanceForms();
    enhanceCurrentSearchPage();
  }

  window.RYASmartSearch = {
    analyzeQuery,
    buildShopifyQuery,
    getQueryVariants,
    smartSuggest,
  };

  initSmartSearch();
  document.addEventListener('DOMContentLoaded', initSmartSearch);
  document.addEventListener('shopify:section:load', initSmartSearch);
})();
